/**
 * Create Campaign Modal
 *
 * Modal form for GMs to create new campaigns.
 * Uses existing campaignStore.createCampaign() backend.
 */

import { useState } from 'react';
import { Icon } from './Icon';
import { useCampaignStore } from '../stores/campaignStore';
import { useAuthStore } from '../stores/authStore';

interface CreateCampaignModalProps {
  onClose: () => void;
  onCreated?: (campaignId: string) => void;
}

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

export function CreateCampaignModal({ onClose, onCreated }: CreateCampaignModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCampaign = useCampaignStore((s) => s.createCampaign);
  const user = useAuthStore((s) => s.user);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Campaign name is required');
      return;
    }

    if (!user?.id) {
      setError('You must be logged in to create a campaign');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const campaign = await createCampaign(user.id, name.trim(), description.trim() || undefined);

    setIsSubmitting(false);

    if (campaign) {
      onCreated?.(campaign.id);
      onClose();
    } else {
      setError('Failed to create campaign. Please try again.');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--radius-md)',
          border: `2px solid ${GOLD_DIM}`,
          padding: 'var(--spacing-xl)',
          width: 440,
          maxWidth: '90vw',
          boxShadow: `0 20px 60px rgba(0, 0, 0, 0.6), 0 0 40px rgba(212, 175, 55, 0.1)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-lg)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-xl)',
              fontFamily: 'var(--font-display)',
              fontWeight: 400,
              color: GOLD,
              letterSpacing: '0.5px',
            }}
          >
            Create New Campaign
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              padding: 'var(--spacing-xs)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="minus" size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Campaign Name */}
          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <label
              htmlFor="campaign-name"
              style={{
                display: 'block',
                marginBottom: 'var(--spacing-xs)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-display)',
                color: 'var(--color-text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              Campaign Name *
            </label>
            <input
              id="campaign-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Curse of Strahd"
              autoFocus
              style={{
                width: '100%',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                fontSize: 'var(--font-size-md)',
                fontFamily: 'var(--font-body)',
                backgroundColor: 'var(--color-surface)',
                border: `1px solid var(--color-border)`,
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <label
              htmlFor="campaign-description"
              style={{
                display: 'block',
                marginBottom: 'var(--spacing-xs)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-display)',
                color: 'var(--color-text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              Description
            </label>
            <textarea
              id="campaign-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A brief description for your players..."
              rows={3}
              style={{
                width: '100%',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                fontSize: 'var(--font-size-md)',
                fontFamily: 'var(--font-body)',
                backgroundColor: 'var(--color-surface)',
                border: `1px solid var(--color-border)`,
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text)',
                outline: 'none',
                resize: 'vertical',
                minHeight: 80,
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* World/Setting placeholder for ISS-0014 */}
          <div
            style={{
              marginBottom: 'var(--spacing-lg)',
              padding: 'var(--spacing-md)',
              backgroundColor: 'var(--color-surface)',
              borderRadius: 'var(--radius-sm)',
              border: '1px dashed var(--color-border)',
              opacity: 0.6,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)',
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <Icon name="sparkle" size={16} />
              <span>World/Setting selection coming soon...</span>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div
              style={{
                marginBottom: 'var(--spacing-md)',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                backgroundColor: 'rgba(220, 20, 60, 0.15)',
                border: '1px solid rgba(220, 20, 60, 0.3)',
                borderRadius: 'var(--radius-sm)',
                color: '#dc143c',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {error}
            </div>
          )}

          {/* Buttons */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--spacing-md)',
              marginTop: 'var(--spacing-lg)',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-display)',
                backgroundColor: 'transparent',
                border: `1px solid var(--color-border)`,
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text-secondary)',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              style={{
                padding: 'var(--spacing-sm) var(--spacing-xl)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                background: `linear-gradient(180deg, ${GOLD} 0%, #a08030 50%, #8b6914 100%)`,
                border: `2px solid ${GOLD}`,
                borderRadius: 'var(--radius-sm)',
                color: '#1a1410',
                cursor: isSubmitting || !name.trim() ? 'not-allowed' : 'pointer',
                opacity: isSubmitting || !name.trim() ? 0.6 : 1,
                boxShadow: `0 2px 8px rgba(212, 175, 55, 0.3)`,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)',
              }}
            >
              {isSubmitting ? (
                <>
                  <Icon name="slow" size={14} />
                  Creating...
                </>
              ) : (
                <>
                  <Icon name="scroll" size={14} />
                  Create Campaign
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
